import { useState, useMemo, useCallback, useEffect } from 'react';
import {  useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Alert,
  Chip,
  Tabs,
  Tab,
  InputLabel,
  Fade
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Router as RouterIcon
} from '@mui/icons-material';
import React from 'react';
import { toast } from 'sonner';

import GlobalSettingsPage from '../Global-Settings/GlobalSettingsPage';
import RoutingFormComponent from '../Deployment-Instance/RoutingFormComponent';
import { useModels } from '../../hooks/useModels';
import { globalSettingsService } from '../../services/GlobalSettingsService';
import { routingService } from '../../services/RoutingService';
import { deploymentInstanceService } from '../../services/DeploymentInstanceService';

export interface DeployedInstance {
  instance_id: string;
  name: string;
  modelId: string;
  model_name: string;
  modelVersion: string;
  instance_status: 'running' | 'stopped' | 'deploying' | 'error';
  endpoint: string;
  region: string;
  instanceType: string;
  deployedAt: Date;
  lastAccessed: Date;
  requestCount: number;
  avgLatency: number;
  uptime: number;
  cost: number;
  replicas: number;
  cpu: number;
  memory: number;
  autoScaling: boolean;
}

type TabValue = 'model-route' | 'global-settings';

export function ModelRouteConfigPage() {
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>('');

  const [routingDetail, setRoutingDetail] = useState<any[]>([]);
  const [deployedInstances, setDeployedInstances] = useState<DeployedInstance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formValue, setFormValue] = useState({
    name: '',
    fallback_model: '',
    utterance: [],
    instance_id: ''
  });
  const { models } = useModels();
  const [routingSettings, setRoutingSettings] = useState({
      key: '', value: [], is_active: false
  });

  useEffect(() => {
    fetchInstanceData();
    fetchRoutingInfo();
    fetchGlobalSettingsData();
  }, []);

  const fetchInstanceData = async () => {
    try {
      const newModel = await deploymentInstanceService.getDeploymentInstances();
      setDeployedInstances(newModel);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching deployment instances:', error);
    }
  };

  const fetchRoutingInfo  = async () => {
    try {
      const result = await routingService.getRoutes();
      // Ensure result is always an array
      if (Array.isArray(result)) {
        setRoutingDetail(result);
      } else if (result) {
        setRoutingDetail([result]);
      } else {
        setRoutingDetail([]);
      }
    } catch (error) {
      console.error('Error fetching routing info:', error);
      setRoutingDetail([]);
    }
  };

  const fetchGlobalSettingsData = async () => {
      try {
          const settings = await globalSettingsService.getList();
          const filteredSettings = settings.filter((setting: { key: string }) => setting.key === 'routing-priority');
          console.log('Filtered Global Routing Settings:', filteredSettings);
          const result = filteredSettings[0];
          result.key = 'routing-priority'
          setRoutingSettings(result);
      } catch (error) {
          toast.error('Failed to fetch global routing settings');
      }
  }

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as TabValue;

  // Active tab
  const [activeTab, setActiveTab] = useState<TabValue>(tabFromUrl || 'model-route');  
  // Get running instances
  const runningInstances = useMemo(() => 
    deployedInstances.filter(d => d.instance_status.toLowerCase() === 'running'),
    [deployedInstances]
  );
  
  // Handle tab change
  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
    setSearchParams({ tab: newValue });
  }, [setSearchParams]);

  // Handle instance selection
  const handleInstanceSelect = (instanceId: string) => {
    setSelectedInstanceId(instanceId);
    const routingForInstance = routingDetail?.filter((r: { instance_id: string }) => r.instance_id === instanceId);
    if (routingForInstance.length > 0) {
      const { name, fallback_model, utterance } = routingForInstance[0];
      const formValue = { name, fallback_model, utterance, instance_id: instanceId }
      setFormValue(formValue);
    } else {
      const name = deployedInstances.filter(i => i.instance_id === instanceId)[0]?.model_name || '';
      setFormValue({
        name,
        fallback_model: '',
        utterance: [],
        instance_id: instanceId
      });
    }
  };

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
            Routing Configuration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure model routing and global settings for intelligent request distribution
          </Typography>
        </Box>

        {/* Main Card with Tabs */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {/* Tabs Header */}
          <Box sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: '#2563eb',
                  height: 3
                },
                px: 3
              }}
            >
              <Tab
                value="model-route"
                icon={<RouterIcon />}
                iconPosition="start"
                label="Model Route Configuration"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.938rem',
                  py: 2,
                  '&.Mui-selected': {
                    color: '#2563eb'
                  }
                }}
              />
              <Tab
                value="global-settings"
                icon={<SettingsIcon />}
                iconPosition="start"
                label="Global Routing Settings"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.938rem',
                  py: 2,
                  '&.Mui-selected': {
                    color: '#2563eb'
                  }
                }}
              />
            </Tabs>
          </Box>

          {/* Tab Content maxHeight: 'calc(100vh - 300px)'*/}
          <Box id="routing-content-scroll" sx={{ p: 4, height: 'auto' , overflowY: 'auto' }}>
            {/* Model Route Configuration Tab */}
            {activeTab === 'model-route' && (
              <Fade in={true}>
                <Stack spacing={3}>
                  {/* Instance Selector */}
                  <Paper elevation={0}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Instance</InputLabel>
                      <Select
                        value={selectedInstanceId}
                        name='selectedInstanceId'
                        onChange={(e, child) => {
                          console.log(e);
                          if (React.isValidElement<{ value?: string }>(child)) {
                            handleInstanceSelect(child.props.value ?? "");
                          }
                        }}
                        label="Select Instance"
                      >
                        <MenuItem value="">
                          <em>Choose an instance...</em>
                        </MenuItem>
                        {runningInstances.map((instance) => (
                          <MenuItem key={instance.instance_id} value={instance.instance_id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {instance.model_name}
                                </Typography>
                              </Box>
                              <Chip 
                                label="running"
                                size="small"
                                sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 600 }}
                              />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    {runningInstances.length === 0 && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        No running instances available. Please deploy a model first.
                      </Alert>
                    )}
                  </Paper>

                  {selectedInstanceId && (
                    <RoutingFormComponent formValue={formValue} currentDeploymentId={selectedInstanceId} models={models} deploymentInstance={runningInstances.find(i => i.instance_id === selectedInstanceId)} routingDetail={undefined} />
                  )}
                </Stack>
              </Fade>
            )}

            {/* Global Settings Tab */}
            {activeTab === 'global-settings' && (
              <GlobalSettingsPage {...routingSettings} />
            )}
          </Box>
        </Paper>


      </Container>
    </Box>
  );
}

export default ModelRouteConfigPage;
